import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from '@/hooks/useInView';
import { ArrowRight, Monitor, Tablet, Smartphone, Users, ShieldCheck, Wifi, BarChart3, ClipboardCheck, GraduationCap, Wrench, Settings } from 'lucide-react';
import { LaptopFrame, TabletFrame, PhoneFrame, QualityDashboard, IoTDashboard, VendorDashboard, MobileChecksheet, AuditScorecard, TrainingDashboard, TraceabilityScreen } from '@/components/DeviceMockups';
import BookDemoDialog from '@/components/BookDemoDialog';

function Section({ children, className = '', ...props }) {
  const [ref, inView] = useInView();
  return <section ref={ref} className={`${inView ? 'animate-fade-in-up' : 'opacity-0'} ${className}`} {...props}>{children}</section>;
}

const deviceUseCases = [
  { device: 'Desktop / Laptop', icon: Monitor, users: 'Plant Heads, Quality Managers, MR, IT', desc: 'Full dashboards, analytics, MIS reports, audit scorecards, and admin configuration. The command center for plant leadership.', dashboards: ['Quality KPI Dashboard', 'Audit Scorecard', 'CAPA Analytics', 'Supplier Ratings', 'Training Overview', 'IoT OEE Monitoring'] },
  { device: 'Tablet', icon: Tablet, users: 'Supervisors, Auditors, SQE Teams', desc: 'Inspection rounds, audit checklists, shopfloor walkthrough entries, VR training modules, and supplier audit forms. Optimized for movement.', dashboards: ['Inspection Forms', 'Audit Checklists', 'Shopfloor Monitor', 'Supplier Audit', 'VR Training', 'Checksheet Entry'] },
  { device: 'Mobile Phone', icon: Smartphone, users: 'Operators, Inspectors, Line Leaders', desc: 'Quick checksheet entries, barcode scanning, pass/fail inspections, safety checklists, and real-time alerts. Designed for hands-on work.', dashboards: ['Digital Checksheet', 'Barcode Scanner', 'Safety Checklist', 'Alerts & Approvals', 'Quick Entry', 'Task Completion'] },
];

const roleDeviceMap = [
  { role: 'Plant Head', primary: 'Desktop', uses: 'MIS dashboards, compliance overviews, audit readiness reports', icon: BarChart3 },
  { role: 'Quality Manager', primary: 'Desktop + Tablet', uses: 'Quality execution, CAPA management, standards control, approvals', icon: ShieldCheck },
  { role: 'Production HOD', primary: 'Desktop', uses: 'Production dashboards, IoT OEE views, downtime analytics', icon: Monitor },
  { role: 'Supervisor / Line Lead', primary: 'Tablet', uses: 'Shopfloor rounds, inspection entries, checksheet reviews', icon: ClipboardCheck },
  { role: 'Auditor (Internal/Process)', primary: 'Tablet', uses: 'Audit checklists, NC logging, finding documentation', icon: ClipboardCheck },
  { role: 'Operator / Inspector', primary: 'Phone', uses: 'Checksheet completion, barcode scanning, pass/fail entry', icon: Smartphone },
  { role: 'SQE / Vendor Manager', primary: 'Desktop + Phone', uses: 'Supplier scorecards, complaint management, CAPA tracking', icon: Users },
  { role: 'Training Coordinator', primary: 'Desktop + Tablet', uses: 'Training schedules, skill matrix, VR module assignment', icon: GraduationCap },
  { role: 'Maintenance Team', primary: 'Tablet + Phone', uses: 'Machine history cards, PM schedules, breakdown logging', icon: Wrench },
  { role: 'IT / System Admin', primary: 'Desktop', uses: 'User management, permissions, ERP integration, system config', icon: Settings },
];

export default function PlatformsPage() {
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <div className="pt-16" data-testid="platforms-page">
      {/* ─── HERO ─── */}
      <section className="relative py-24 lg:py-32 overflow-hidden bg-white">
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #000 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#FEF2F2] border border-[#FECACA] rounded-full px-4 py-1.5 mb-6">
              <Monitor className="w-3.5 h-3.5 text-[#C41E3A]" />
              <span className="text-xs font-semibold text-[#C41E3A] uppercase tracking-wider">Cross-Platform</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-[#111827]" data-testid="platforms-hero-title">
              Access <span className="tracking-wide">MUSHIN</span> from any device
            </h1>
            <p className="mt-7 text-xl text-[#4B5563] leading-relaxed">
              Desktop dashboards for plant leadership. Tablet forms for supervisors on the floor. Phone checksheets for operators at the line. <span className="font-bold text-[#111827]">MUSHIN</span> works where your team works.
            </p>
          </div>
        </div>
      </section>

      {/* ─── DEVICE SHOWCASE ─── */}
      <Section className="py-24 lg:py-32 bg-[#FAFAFA] border-y border-[#F0F0F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium text-center">Platform Experience</p>
          <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-[#111827] font-heading text-center">
            One platform. Every screen size.
          </h2>
          <p className="mt-5 text-lg text-[#4B5563] text-center max-w-2xl mx-auto">
            Mushin adapts its interface to the device. Full analytics on desktop, actionable forms on tablet, quick entries on phone.
          </p>

          {/* Three-device display */}
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end" data-testid="device-showcase">
            {/* Laptop */}
            <div className="lg:col-span-7">
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#9CA3AF] mb-3 text-center">Desktop Dashboard</p>
              <LaptopFrame>
                <QualityDashboard />
                <div className="px-3 pb-2">
                  <div className="grid grid-cols-3 gap-1.5">
                    <div className="bg-[#F9FAFB] rounded p-1.5 border border-[#F3F4F6]">
                      <div className="text-[7px] text-[#9CA3AF] uppercase">Active Users</div>
                      <div className="text-[10px] font-bold text-[#111827]">147 online</div>
                    </div>
                    <div className="bg-[#F9FAFB] rounded p-1.5 border border-[#F3F4F6]">
                      <div className="text-[7px] text-[#9CA3AF] uppercase">Today's Entries</div>
                      <div className="text-[10px] font-bold text-[#111827]">2,341</div>
                    </div>
                    <div className="bg-[#F9FAFB] rounded p-1.5 border border-[#F3F4F6]">
                      <div className="text-[7px] text-[#9CA3AF] uppercase">Compliance</div>
                      <div className="text-[10px] font-bold text-[#059669]">99.1%</div>
                    </div>
                  </div>
                </div>
              </LaptopFrame>
            </div>
            {/* Tablet */}
            <div className="lg:col-span-3">
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#9CA3AF] mb-3 text-center">Tablet</p>
              <TabletFrame>
                <AuditScorecard />
              </TabletFrame>
            </div>
            {/* Phone */}
            <div className="lg:col-span-2 max-w-[180px] mx-auto">
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#9CA3AF] mb-3 text-center">Phone</p>
              <PhoneFrame>
                <MobileChecksheet />
              </PhoneFrame>
            </div>
          </div>
        </div>
      </Section>

      {/* ─── DEVICE USE CASES ─── */}
      <Section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">Device-Optimized</p>
          <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-[#111827] font-heading">
            The right interface for every role
          </h2>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-8">
            {deviceUseCases.map((d) => (
              <div key={d.device} className="bg-[#FAFAFA] border border-[#E5E7EB] rounded-2xl p-7 hover:shadow-md transition-all" data-testid={`device-usecase-${d.device}`}>
                <div className="w-12 h-12 rounded-xl bg-[#111827] flex items-center justify-center mb-5">
                  <d.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#111827]">{d.device}</h3>
                <p className="text-xs text-[#C41E3A] font-medium mt-1 mb-3">{d.users}</p>
                <p className="text-sm text-[#4B5563] leading-relaxed mb-5">{d.desc}</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {d.dashboards.map((db) => (
                    <div key={db} className="text-[10px] text-[#374151] bg-white border border-[#E5E7EB] rounded-lg px-2.5 py-1.5 font-medium">{db}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ─── DASHBOARD GALLERY ─── */}
      <Section className="py-24 lg:py-32 bg-[#111827]" data-testid="dashboard-gallery">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">Dashboard Gallery</p>
          <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-white font-heading">
            Every module. Every dashboard.
          </h2>
          <p className="mt-4 text-[#9CA3AF] max-w-2xl">From quality execution to IoT machine monitoring: see how Mushin visualizes your plant data.</p>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: 'Quality Dashboard', Comp: QualityDashboard },
              { title: 'IoT & Machine Data', Comp: IoTDashboard },
              { title: 'Vendor Connect', Comp: VendorDashboard },
              { title: 'Audit Scorecard', Comp: AuditScorecard },
              { title: 'Training / DOJO', Comp: TrainingDashboard },
              { title: 'End-to-End Traceability', Comp: TraceabilityScreen },
            ].map((d) => (
              <div key={d.title} className="bg-white rounded-xl overflow-hidden shadow-lg shadow-black/20 hover:-translate-y-1 transition-transform" data-testid={`gallery-dashboard-${d.title}`}>
                <div className="flex items-center gap-1.5 px-3 py-2 bg-[#F3F4F6] border-b border-[#E5E7EB]">
                  <div className="w-2 h-2 rounded-full bg-[#EF4444]/50" />
                  <div className="w-2 h-2 rounded-full bg-[#F59E0B]/50" />
                  <div className="w-2 h-2 rounded-full bg-[#22C55E]/50" />
                  <span className="ml-auto text-[8px] text-[#9CA3AF] font-mono">{d.title}</span>
                </div>
                <d.Comp />
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ─── ROLE-DEVICE MAP ─── */}
      <Section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">Role-Based Access</p>
          <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-[#111827] font-heading">
            Every role. The right device. The right data.
          </h2>
          <p className="mt-4 text-base text-[#4B5563] max-w-2xl">Mushin adapts to how each team member works, giving operators quick mobile forms and plant heads comprehensive desktop analytics.</p>

          <div className="mt-14 overflow-x-auto">
            <table className="w-full min-w-[600px]" data-testid="role-device-table">
              <thead>
                <tr className="border-b-2 border-[#E5E7EB]">
                  <th className="py-3 text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Role</th>
                  <th className="py-3 text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Primary Device</th>
                  <th className="py-3 text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Key Uses</th>
                </tr>
              </thead>
              <tbody>
                {roleDeviceMap.map((r, i) => (
                  <tr key={i} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA] transition-colors" data-testid={`role-row-${i}`}>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-[#FEF2F2] flex items-center justify-center flex-shrink-0">
                          <r.icon className="w-3.5 h-3.5 text-[#C41E3A]" />
                        </div>
                        <span className="text-[15px] font-semibold text-[#111827]">{r.role}</span>
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-[#F3F4F6] text-[#374151]">{r.primary}</span>
                    </td>
                    <td className="py-3">
                      <span className="text-[15px] text-[#4B5563]">{r.uses}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      {/* ─── MULTI-DEVICE FEATURE STRIP ─── */}
      <Section className="py-12 border-y border-[#F0F0F0] bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {[
              { label: 'Responsive UI', desc: 'Adapts to any screen size' },
              { label: 'Offline Capable', desc: 'Key features work without network' },
              { label: 'Fast Loading', desc: 'Optimized for plant connectivity' },
              { label: 'Secure Access', desc: 'Role-based on every device' },
            ].map((f, i) => (
              <div key={i} className="text-center" data-testid={`platform-feature-${i}`}>
                <div className="text-sm font-bold text-[#111827]">{f.label}</div>
                <div className="text-[13px] text-[#6B7280] mt-1">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ─── CTA ─── */}
      <section className="py-24 lg:py-32 bg-[#111827]" data-testid="platforms-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-white font-heading max-w-2xl mx-auto">
            See <span className="tracking-wide">MUSHIN</span> in action on your device
          </h2>
          <p className="mt-5 text-lg text-[#9CA3AF] max-w-xl mx-auto">Book a live demo and experience the platform on desktop, tablet, or phone. Exactly how your teams would use it daily.</p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <button onClick={() => setDemoOpen(true)} className="bg-[#C41E3A] hover:bg-[#A11830] text-white px-8 py-3.5 rounded-full text-[15px] font-semibold transition-colors inline-flex items-center gap-2" data-testid="platforms-cta-demo">
              Book a Demo <ArrowRight className="w-4 h-4" />
            </button>
            <a href="mailto:sales@mushinlabs.com" className="border border-[#374151] hover:border-[#4B5563] text-white px-8 py-3.5 rounded-full text-[15px] font-semibold transition-colors" data-testid="platforms-cta-quote">
              Get a Quote
            </a>
          </div>
        </div>
      </section>

      <BookDemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}
